/**
 * Delivery Checker — Quoted Dhaaga
 *
 * Calls Delhivery serviceability API to estimate delivery window.
 * Processing time: standard 2-3 days, customised 3-4 days.
 * Skips Sundays when projecting delivery dates.
 */

const CACHE_KEY = 'qd_delivery_cache';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

// Fallback TAT when API is unreachable — purely pincode-range based
const METRO_RANGES = [
  [110001, 110096],  // Delhi
  [400001, 400107],  // Mumbai
  [560001, 560103],  // Bengaluru
  [500001, 500100],  // Hyderabad
  [411001, 411067],  // Pune
  [600001, 600119],  // Chennai
  [700001, 700160],  // Kolkata
  [226001, 226030],  // Lucknow
  [208001, 208027],  // Kanpur
  [302001, 302040],  // Jaipur
  [380001, 380063],  // Ahmedabad
  [122001, 122018],  // Gurugram
  [201301, 201310],  // Noida
];

function isMetro(pin) {
  const n = parseInt(pin, 10);
  return METRO_RANGES.some(([lo, hi]) => n >= lo && n <= hi);
}

function fallbackTAT(pin) {
  return isMetro(pin) ? { min: 2, max: 4 } : { min: 3, max: 6 };
}

function addBusinessDays(date, days) {
  const d = new Date(date);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0) added++; // skip Sundays
  }
  return d;
}

function formatDate(date) {
  return date.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function getCache(pin) {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cache = JSON.parse(raw);
    const entry = cache[pin];
    if (!entry) return null;
    if (Date.now() - entry.ts > CACHE_TTL) return null;
    return entry.data;
  } catch {
    return null;
  }
}

function setCache(pin, data) {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    const cache = raw ? JSON.parse(raw) : {};
    cache[pin] = { ts: Date.now(), data };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // storage unavailable — skip cache
  }
}

async function fetchServiceability(token, pin) {
  const cached = getCache(pin);
  if (cached) return cached;

  const url = `https://track.delhivery.com/c/api/pin-codes/json/?token=${token}&filter_codes=${pin}`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  const result = { raw: json };
  setCache(pin, result);
  return result;
}

function parseTAT(raw) {
  const codes = raw?.delivery_codes;
  if (!Array.isArray(codes) || codes.length === 0) return null;

  const postal = codes[0]?.postal_code;
  if (!postal) return null;

  const serviceable = postal.pre_paid === 'Y';
  if (!serviceable) return { serviceable: false };

  const isODA = postal.is_oda === 'Y';
  const speed = postal.max_speed;

  let min, max;
  if (speed === 'E' && !isODA)  { min = 2; max = 4; }
  else if (speed === 'E' && isODA) { min = 3; max = 6; }
  else if (speed === 'D' && !isODA) { min = 4; max = 7; }
  else                              { min = 5; max = 9; }

  return { serviceable: true, min, max, isODA };
}

class DeliveryChecker extends HTMLElement {
  connectedCallback() {
    this._token     = this.dataset.token     || '';
    this._isCustom  = this.dataset.custom    === 'true';
    this._procMin   = this._isCustom ? 3 : 2;
    this._procMax   = this._isCustom ? 4 : 3;

    this._input  = this.querySelector('[data-dc-input]');
    this._btn    = this.querySelector('[data-dc-btn]');
    this._result = this.querySelector('[data-dc-result]');

    this._btn.addEventListener('click', () => this._check());
    this._input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this._check();
    });
    this._input.addEventListener('input', () => this._clearResult());
  }

  _clearResult() {
    this._result.innerHTML = '';
    this._result.className = 'dc-result';
  }

  async _check() {
    const pin = this._input.value.trim();
    if (!/^\d{6}$/.test(pin)) {
      this._showError('Please enter a valid 6-digit pincode.');
      return;
    }

    this._setLoading(true);

    let tat;
    try {
      const raw = await fetchServiceability(this._token, pin);
      tat = parseTAT(raw.raw);
    } catch {
      // API unreachable (CORS or network) — use fallback
      tat = { serviceable: true, ...fallbackTAT(pin), fallback: true };
    }

    this._setLoading(false);

    if (!tat) {
      this._showError('Unable to check serviceability. Please try again.');
      return;
    }

    if (tat.serviceable === false) {
      this._showError('Sorry, we don\'t deliver to this pincode yet.');
      return;
    }

    this._showDelivery(pin, tat);
  }

  _showDelivery(pin, tat) {
    // Cutoff: orders placed after 3 PM IST go to next day for processing
    const now = new Date();
    const istHour = (now.getUTCHours() + 5) % 24 + (now.getUTCMinutes() >= 30 ? 0.5 : 0);
    const baseDate = istHour >= 15 ? new Date(now.getTime() + 86400000) : now;

    const earliest = addBusinessDays(baseDate, this._procMin + tat.min);
    const latest   = addBusinessDays(baseDate, this._procMax + tat.max);

    const sameDay = earliest.toDateString() === latest.toDateString();
    const dateStr = sameDay
      ? formatDate(earliest)
      : `${formatDate(earliest)} – ${formatDate(latest)}`;

    const odaNote = tat.isODA ? ' (extended delivery area)' : '';

    this._result.innerHTML = `
      <span class="dc-result__icon">📦</span>
      <span class="dc-result__text">
        <strong>Estimated delivery: ${dateStr}</strong>${odaNote}
        <small>${this._procMin}–${this._procMax} days processing + ${tat.min}–${tat.max} days shipping</small>
      </span>
    `;
    this._result.className = 'dc-result dc-result--success';
  }

  _showError(msg) {
    this._result.innerHTML = `<span class="dc-result__icon">⚠️</span><span class="dc-result__text">${msg}</span>`;
    this._result.className = 'dc-result dc-result--error';
  }

  _setLoading(on) {
    this._btn.disabled   = on;
    this._input.disabled = on;
    this._btn.textContent = on ? '…' : 'Check';
  }
}

customElements.define('delivery-checker', DeliveryChecker);
